/**
 * InstanceAuditLogDrawer - Drawer para visualização do histórico de auditoria
 * 
 * Exibe cronologia de eventos para uma instância específica,
 * incluindo alterações na instância e nas inboxes vinculadas.
 * Layout estilo Stripe com drawer de baixo para cima.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "@/components/ui/drawer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  History,
  Plus,
  Edit,
  Link,
  Unlink,
  Key,
  Trash2,
  CheckCircle,
  XCircle,
  Send,
  ChevronDown,
  ChevronRight,
  User,
  Bot,
  Shield,
  Inbox,
  Server,
  RefreshCw,
  X,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

// ============================================
// TIPOS E INTERFACES
// ============================================

interface AuditLogEntry {
  id: string;
  instance_id: string | null;
  inbox_id: string | null;
  tenant_id: string;
  entity_type: "instance" | "inbox";
  action_type: string;
  old_status: string | null;
  new_status: string | null;
  old_values: Record<string, unknown>;
  new_values: Record<string, unknown>;
  changed_fields: Record<string, { old?: unknown; new?: unknown } | null> | string[] | null;
  metadata: Record<string, unknown>;
  performed_by: string | null;
  performed_by_type: "admin" | "tenant" | "system";
  created_at: string;
  performer_name?: string;
  performer_email?: string;
}

interface InstanceAuditLogDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instanceId: string;
  instanceName?: string;
}

// ============================================
// CONFIGURAÇÕES DE EXIBIÇÃO
// ============================================

const actionConfig: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string; label: string }> = {
  created: { icon: Plus, color: "text-green-500", label: "Criado" },
  updated: { icon: Edit, color: "text-blue-500", label: "Atualizado" },
  status_changed: { icon: RefreshCw, color: "text-yellow-500", label: "Status Alterado" },
  linked: { icon: Link, color: "text-purple-500", label: "Vinculado" },
  unlinked: { icon: Unlink, color: "text-orange-500", label: "Desvinculado" },
  credentials_updated: { icon: Key, color: "text-cyan-500", label: "Credenciais" },
  deleted: { icon: Trash2, color: "text-red-500", label: "Excluído" },
  activated: { icon: CheckCircle, color: "text-green-500", label: "Ativado" },
  deactivated: { icon: XCircle, color: "text-red-500", label: "Desativado" },
  released: { icon: Send, color: "text-indigo-500", label: "Liberado" },
};

const performerConfig: Record<string, { icon: React.ComponentType<{ className?: string }>; label: string }> = {
  admin: { icon: Shield, label: "Admin" },
  tenant: { icon: User, label: "Tenant" },
  system: { icon: Bot, label: "Sistema" },
};

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export function InstanceAuditLogDrawer({
  open,
  onOpenChange,
  instanceId,
  instanceName,
}: InstanceAuditLogDrawerProps) {
  const [entityFilter, setEntityFilter] = useState<string>("all");
  const [actionFilter, setActionFilter] = useState<string>("all");

  const { data: logs, isLoading, refetch } = useQuery({
    queryKey: ["instance-audit-log", instanceId, entityFilter, actionFilter],
    queryFn: async () => {
      let query = supabase
        .from("instance_audit_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);

      query = query.or(`instance_id.eq.${instanceId},and(entity_type.eq.inbox,instance_id.eq.${instanceId})`);

      if (entityFilter !== "all") {
        query = query.eq("entity_type", entityFilter);
      }

      if (actionFilter !== "all") {
        query = query.eq("action_type", actionFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      const performerIds = [...new Set((data || [])
        .filter(log => log.performed_by)
        .map(log => log.performed_by))] as string[];

      let performersMap: Record<string, { name: string; email: string }> = {};

      if (performerIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, full_name, email")
          .in("id", performerIds);

        performersMap = (profiles || []).reduce((acc, p) => {
          acc[p.id] = { name: p.full_name || "Usuário", email: p.email || "" };
          return acc;
        }, {} as Record<string, { name: string; email: string }>);
      }

      return (data || []).map(log => ({
        ...log,
        performer_name: log.performed_by ? performersMap[log.performed_by]?.name : undefined,
        performer_email: log.performed_by ? performersMap[log.performed_by]?.email : undefined,
      })) as AuditLogEntry[];
    },
    enabled: open && !!instanceId,
  });

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <div className="mx-auto w-full max-w-4xl">
          {/* Cabeçalho */}
          <DrawerHeader className="flex flex-row items-center justify-between border-b pb-4">
            <DrawerTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Histórico de Auditoria
              {instanceName && (
                <Badge variant="outline" className="ml-2 font-mono">
                  {instanceName}
                </Badge>
              )}
            </DrawerTitle>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon">
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </DrawerHeader>

          {/* Conteúdo */}
          <div className="p-6 space-y-4">
            {/* Filtros */}
            <div className="flex items-center gap-3">
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Entidade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="instance">Instância</SelectItem>
                  <SelectItem value="inbox">Inbox</SelectItem>
                </SelectContent>
              </Select>

              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Ação" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas Ações</SelectItem>
                  <SelectItem value="created">Criado</SelectItem>
                  <SelectItem value="updated">Atualizado</SelectItem>
                  <SelectItem value="status_changed">Status</SelectItem>
                  <SelectItem value="linked">Vinculado</SelectItem>
                  <SelectItem value="unlinked">Desvinculado</SelectItem>
                  <SelectItem value="credentials_updated">Credenciais</SelectItem>
                  <SelectItem value="activated">Ativado</SelectItem>
                  <SelectItem value="deactivated">Desativado</SelectItem>
                  <SelectItem value="released">Liberado</SelectItem>
                  <SelectItem value="deleted">Excluído</SelectItem>
                </SelectContent>
              </Select>

              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Atualizar
              </Button>
            </div>

            {/* Lista de eventos */}
            <ScrollArea className="h-[55vh] pr-4">
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex gap-4">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : logs && logs.length > 0 ? (
                <div className="space-y-1">
                  {logs.map((log) => (
                    <AuditLogItem key={log.id} log={log} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <History className="h-12 w-12 mb-4 opacity-50" />
                  <p>Nenhum evento encontrado</p>
                  <p className="text-sm">Os eventos aparecerão aqui conforme alterações forem feitas</p>
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

// ============================================
// COMPONENTE DE ITEM DE LOG
// ============================================

function AuditLogItem({ log }: { log: AuditLogEntry }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const config = actionConfig[log.action_type] || actionConfig.updated;
  const performerCfg = performerConfig[log.performed_by_type] || performerConfig.system;
  const ActionIcon = config.icon;
  const PerformerIcon = performerCfg.icon;
  const EntityIcon = log.entity_type === "instance" ? Server : Inbox;

  const formattedDate = format(new Date(log.created_at), "dd/MM/yyyy HH:mm:ss", { locale: ptBR });

  const getDescription = () => {
    const entity = log.entity_type === "instance" ? "Instância" : "Inbox";
    
    switch (log.action_type) {
      case "created":
        return `${entity} criada`;
      case "updated":
        const fields = getChangedFieldNames(log.changed_fields);
        return `${entity} atualizada${fields.length > 0 ? ` (${fields.join(", ")})` : ""}`;
      case "status_changed":
        return `Status alterado: ${log.old_status} → ${log.new_status}`;
      case "linked":
        return `Inbox vinculada à instância`;
      case "unlinked":
        return `Inbox desvinculada da instância`;
      case "credentials_updated":
        return `Credenciais atualizadas`;
      case "activated":
        return `${entity} ativada`;
      case "deactivated":
        return `${entity} desativada`;
      case "released":
        return `Instância liberada para uso`;
      case "deleted":
        return `${entity} excluída`;
      default:
        return `${entity} modificada`;
    }
  };

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
        {/* Ícone da ação */}
        <div className={`p-2 rounded-full bg-muted ${config.color}`}>
          <ActionIcon className="h-4 w-4" />
        </div>

        {/* Conteúdo */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-xs">
              <EntityIcon className="h-3 w-3 mr-1" />
              {log.entity_type === "instance" ? "Instância" : "Inbox"}
            </Badge>

            <Badge variant="secondary" className="text-xs">
              {config.label}
            </Badge>

            {(() => {
              const changedFieldNames = getChangedFieldNames(log.changed_fields);
              return changedFieldNames.length > 0 && log.action_type === "updated" && (
                <span className="text-xs text-muted-foreground">
                  ({changedFieldNames.slice(0, 3).join(", ")}
                  {changedFieldNames.length > 3 && ` +${changedFieldNames.length - 3}`})
                </span>
              );
            })()}
          </div>

          <p className="text-sm mt-1">{getDescription()}</p>

          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            <span>{formattedDate}</span>
            
            <span className="flex items-center gap-1">
              <PerformerIcon className="h-3 w-3" />
              {log.performer_name || performerCfg.label}
              {log.performer_email && (
                <span className="opacity-70">({log.performer_email})</span>
              )}
            </span>
          </div>
        </div>

        {/* Botão expandir */}
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </CollapsibleTrigger>
      </div>

      {/* Detalhes expandidos com visualização de diff */}
      <CollapsibleContent>
        <div className="ml-12 p-3 bg-muted/30 rounded-lg text-xs space-y-3">
          {/* Visualização de Diff */}
          {(Object.keys(log.old_values || {}).length > 0 || Object.keys(log.new_values || {}).length > 0) && (
            <DiffViewer oldValues={log.old_values} newValues={log.new_values} />
          )}

          {/* Metadata (contexto adicional) */}
          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <div>
              <p className="text-muted-foreground mb-1 font-sans font-medium">Contexto:</p>
              <pre className="whitespace-pre-wrap break-all bg-background p-2 rounded text-[11px] font-mono">
                {JSON.stringify(log.metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ============================================
// FUNÇÕES UTILITÁRIAS
// ============================================

/**
 * Extrai nomes dos campos alterados de changed_fields
 * Suporta tanto objeto JSONB quanto array de strings
 */
function getChangedFieldNames(changedFields: unknown): string[] {
  if (!changedFields) return [];
  if (Array.isArray(changedFields)) return changedFields;
  if (typeof changedFields === 'object') {
    return Object.keys(changedFields).filter(key => {
      const value = (changedFields as Record<string, unknown>)[key];
      return value !== null && value !== undefined;
    });
  }
  return [];
}

/**
 * Filtra dados sensíveis para não exibir tokens/senhas
 */
function filterSensitiveData(data: Record<string, unknown>): Record<string, unknown> {
  const sensitiveFields = ["token", "password", "secret", "api_key", "credentials"];
  const filtered = { ...data };
  
  for (const key of Object.keys(filtered)) {
    if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
      filtered[key] = "***REDACTED***";
    }
  }
  
  return filtered;
}

// ============================================
// TIPOS E FUNÇÕES PARA DIFF
// ============================================

interface DiffEntry {
  field: string;
  oldValue: unknown;
  newValue: unknown;
  changeType: "added" | "removed" | "modified" | "unchanged";
}

/**
 * Compara dois objetos e retorna as diferenças campo a campo
 */
function computeDiff(
  oldValues: Record<string, unknown>,
  newValues: Record<string, unknown>
): DiffEntry[] {
  const allKeys = new Set([...Object.keys(oldValues), ...Object.keys(newValues)]);
  const diff: DiffEntry[] = [];
  
  for (const key of allKeys) {
    const oldVal = oldValues[key];
    const newVal = newValues[key];
    
    if (!(key in oldValues)) {
      diff.push({ field: key, oldValue: undefined, newValue: newVal, changeType: "added" });
    } else if (!(key in newValues)) {
      diff.push({ field: key, oldValue: oldVal, newValue: undefined, changeType: "removed" });
    } else if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      diff.push({ field: key, oldValue: oldVal, newValue: newVal, changeType: "modified" });
    }
    // Campos inalterados são ignorados para exibição limpa
  }
  
  return diff;
}

/**
 * Formata valor para exibição na tabela de diff
 */
function formatValue(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

// ============================================
// COMPONENTE DIFF VIEWER
// ============================================

/**
 * DiffViewer - Visualização comparativa de alterações
 * Exibe campos alterados em formato de tabela com destaque vermelho/verde
 */
function DiffViewer({ 
  oldValues, 
  newValues 
}: { 
  oldValues: Record<string, unknown>; 
  newValues: Record<string, unknown>;
}) {
  const diff = computeDiff(
    filterSensitiveData(oldValues),
    filterSensitiveData(newValues)
  );
  
  if (diff.length === 0) {
    return (
      <p className="text-muted-foreground text-sm italic">
        Nenhuma alteração detectada
      </p>
    );
  }
  
  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-xs font-sans font-medium">
        Alterações Detectadas ({diff.length} {diff.length === 1 ? "campo" : "campos"})
      </p>
      
      <div className="rounded-md border overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left font-medium w-1/4">Campo</th>
              <th className="px-3 py-2 text-left font-medium w-[37.5%]">Anterior</th>
              <th className="px-3 py-2 text-left font-medium w-[37.5%]">Atual</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {diff.map((entry) => (
              <tr key={entry.field}>
                <td className="px-3 py-2 font-mono font-medium text-foreground">
                  {entry.field}
                </td>
                <td className={cn(
                  "px-3 py-2 font-mono",
                  entry.changeType !== "added" && "bg-destructive/10 text-destructive"
                )}>
                  {entry.changeType === "added" ? (
                    <span className="text-muted-foreground italic">—</span>
                  ) : (
                    <span className="break-all">{formatValue(entry.oldValue)}</span>
                  )}
                </td>
                <td className={cn(
                  "px-3 py-2 font-mono",
                  entry.changeType !== "removed" && "bg-green-500/10 text-green-700 dark:text-green-400"
                )}>
                  {entry.changeType === "removed" ? (
                    <span className="text-muted-foreground italic">—</span>
                  ) : (
                    <span className="break-all">{formatValue(entry.newValue)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
